import { defineStore } from 'pinia';
import { uselocalStorageGetterSetter } from '@/composables/uselocalStorageGetterSetter';
import { useUserDetailsStore } from '@/stores/useUserDetails';
import { onboardingCategories } from '@/constants/onboardingChecklist';

/** completed/total tally over `itemIds`, plus the derived percent complete. */
const progressOf = (itemIds, itemCompletion) => {
  const completed = itemIds.filter((itemId) => itemCompletion[itemId]).length;
  const total = itemIds.length;
  return { completed, total, percent: total === 0 ? 0 : Math.round((completed / total) * 100) };
};

const ITEM_COMPLETION_STORAGE_KEY = 'GET_STARTED_ITEM_COMPLETION';
const DONT_SHOW_AGAIN_STORAGE_KEY = 'GET_STARTED_DONT_SHOW_AGAIN';

/** Reads and parses a key, falling back rather than throwing on missing or malformed JSON. */
const readWithFallback = (getParsedObject, key, fallback) => {
  try {
    return getParsedObject(key) || fallback;
  } catch {
    return fallback;
  }
};

export const useGetStartedStore = defineStore('getStartedStore', {
  state: () => {
    return {
      // Maps a checklist item id to `true` once it has been completed in this browser.
      // e.g. { 'staging-a-job': true }
      itemCompletion: {},
      // Permanent, per-browser dismissal — once set, the widget never auto-shows again.
      dontShowAgain: false,
    };
  },
  actions: {
    hydrate() {
      const { getParsedObject } = uselocalStorageGetterSetter();

      this.itemCompletion = readWithFallback(getParsedObject, ITEM_COMPLETION_STORAGE_KEY, {});
      this.dontShowAgain = readWithFallback(getParsedObject, DONT_SHOW_AGAIN_STORAGE_KEY, false);
    },
    persistItemCompletion() {
      const { setObject } = uselocalStorageGetterSetter();
      setObject(ITEM_COMPLETION_STORAGE_KEY, this.itemCompletion);
    },
    persistDontShowAgain() {
      const { setObject } = uselocalStorageGetterSetter();
      setObject(DONT_SHOW_AGAIN_STORAGE_KEY, this.dontShowAgain);
    },
    /**
     * Mark a single checklist item complete and persist immediately. Idempotent: calling it
     * again for an already-complete item is a no-op, matching `useFeatureAnnouncementsStore`'s
     * `markSeen` precedent. Callers (a walkthrough's real `onComplete`) never un-complete an
     * item, so there's no companion "uncomplete" action.
     */
    markItemComplete(itemId) {
      if (this.itemCompletion[itemId]) {
        return;
      }
      this.itemCompletion = { ...this.itemCompletion, [itemId]: true };
      this.persistItemCompletion();
    },
    /** Permanently hide the widget for this browser, independent of `hasUserOnboarded` and item completion. */
    dismissPermanently() {
      this.dontShowAgain = true;
      this.persistDontShowAgain();
    },
  },
  getters: {
    /**
     * All authored item ids across every category — the single source `overallProgress`
     * counts against, so the total is never a hardcoded number.
     */
    allItemIds() {
      return onboardingCategories.flatMap((category) => category.topicIds);
    },
    /** `{ completed, total, percent }` across every authored item. */
    overallProgress() {
      return progressOf(this.allItemIds, this.itemCompletion);
    },
    /** `{ completed, total, percent }` for one category, or all-zero for an unknown id. */
    categoryProgress() {
      return (categoryId) => {
        const category = onboardingCategories.find((c) => c.id === categoryId);
        return progressOf(category?.topicIds ?? [], this.itemCompletion);
      };
    },
    /**
     * Backend-owned onboarded state, off the existing user details fetch rather than a
     * separate call. Falls back to `false` (still eligible) while the field is absent, so
     * this doesn't hard-depend on the backend field shipping in the same window.
     */
    hasUserOnboarded() {
      return useUserDetailsStore().user?.hasUserOnboarded ?? false;
    },
    /**
     * The resolved OR-gate (ADR 0003): hidden once any one of the three signals fires, so no
     * consumer needs to know or combine them itself.
     */
    shouldShowWidget() {
      return !(
        this.hasUserOnboarded ||
        this.overallProgress.percent === 100 ||
        this.dontShowAgain
      );
    },
  },
});
